import React, { useRef, useState } from "react";
import axios from "axios";
import { Download, Trash2, Upload, FileText } from "lucide-react";
import VideoUpload from "../VideoUpload";
import { resolveImageUrl } from "../../lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const box = { border: "1px solid #f1f5f9", borderRadius: 10, padding: "12px 14px", marginTop: 12 };
const head = { fontFamily: "Manrope", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 2 };
const hintStyle = { fontFamily: "Manrope", fontSize: 11, color: "#9ca3af", marginBottom: 10 };
const btn = {
  display: "inline-flex", alignItems: "center", gap: 6, background: "white",
  border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 12px",
  fontFamily: "Manrope", fontSize: 12, cursor: "pointer", color: "#374151",
};

const isImage = name => /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(name || "");

/** Фото «ДО» / «ПОСЛЕ» и подписанный договор. Видно только в админке. */
export function AtelierMedia({ orderId, attachments = [], kind, label, hint, accept, onUpdate }) {
  const items = attachments.filter(a => a.kind === kind);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true); setError("");
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        const r = await axios.post(`${API}/admin/atelier/orders/${orderId}/attachments`,
          fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        onUpdate(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось загрузить файл");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить файл?")) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/atelier/orders/${orderId}/attachments/${id}`,
        { withCredentials: true });
      onUpdate(r.data);
    } finally { setBusy(false); }
  };

  return (
    <div style={box} data-testid={`atelier-media-${kind}`}>
      <div style={head}>{label} <span style={{ color: "#9ca3af", fontWeight: 500 }}>({items.length})</span></div>
      {hint && <div style={hintStyle}>{hint}</div>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: items.length ? 10 : 0 }}>
        {items.map(a => (
          <div key={a.id} data-testid={`atelier-file-${a.id}`}
            style={{ width: 104, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fafafa" }}>
            {isImage(a.name) ? (
              <a href={resolveImageUrl(a.url)} target="_blank" rel="noreferrer">
                <img src={resolveImageUrl(a.url, { w: 300 })} alt={a.name}
                  style={{ width: "100%", height: 104, objectFit: "cover", display: "block" }} />
              </a>
            ) : (
              <div style={{ height: 104, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileText size={28} color="#9ca3af" />
              </div>
            )}
            <div style={{ padding: "6px 6px 8px" }}>
              <div style={{ fontSize: 10, color: "#6b7280", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>{a.name}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <a href={resolveImageUrl(a.url)} download={a.name} target="_blank" rel="noreferrer"
                  data-testid={`atelier-file-download-${a.id}`}
                  style={{ color: "#2563eb", display: "inline-flex" }} title="Скачать">
                  <Download size={13} />
                </a>
                <button onClick={() => remove(a.id)} disabled={busy}
                  data-testid={`atelier-file-del-${a.id}`}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#dc2626" }}
                  title="Удалить">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <input ref={fileRef} type="file" multiple accept={accept} hidden
        data-testid={`atelier-upload-input-${kind}`}
        onChange={(e) => upload(e.target.files)} />
      <button style={btn} disabled={busy} data-testid={`atelier-upload-${kind}`}
        onClick={() => fileRef.current?.click()}>
        <Upload size={13} /> {busy ? "Загружаем…" : "Загрузить"}
      </button>
      {error && <div style={{ color: "#dc2626", fontSize: 11.5, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

/** Видео с пояснениями мастеру — через тот же R2-пайплайн, что и уроки (до 1 ГБ). */
export function AtelierVideoSection({ orderId, attachments = [], onUpdate }) {
  const items = attachments.filter(a => a.kind === "video");
  const [busy, setBusy] = useState(false);

  const handleChange = async (next) => {
    setBusy(true);
    try {
      const added = next.find(v => !items.some(a => a.url === v.url));
      if (added) {
        const r = await axios.post(`${API}/admin/atelier/orders/${orderId}/attachments/link`,
          { kind: "video", url: added.url, poster: added.poster,
            size: added.size, name: added.original_name || "video.mp4" },
          { withCredentials: true });
        onUpdate(r.data);
        return;
      }
      const removed = items.find(a => !next.some(v => v.url === a.url));
      if (removed) {
        const r = await axios.delete(
          `${API}/admin/atelier/orders/${orderId}/attachments/${removed.id}`,
          { withCredentials: true });
        onUpdate(r.data);
      }
    } finally { setBusy(false); }
  };

  return (
    <div style={box} data-testid="atelier-media-video">
      <div style={head}>Видео с пояснениями <span style={{ color: "#9ca3af", fontWeight: 500 }}>({items.length})</span></div>
      <div style={hintStyle}>Что именно переделать — объяснение для мастера. Видно только сотрудникам.</div>
      <VideoUpload
        testId="atelier-video-upload"
        label="Видео по заявке"
        hint={busy ? "Сохраняем…" : "Ролик с пояснениями по перешиву"}
        maxVideos={3}
        value={items.map(a => ({ url: a.url, poster: a.poster, size: a.size, original_name: a.name }))}
        onChange={handleChange}
      />
    </div>
  );
}
